/**
 * **Le contrat d'Atelier, côté fil** — ce que `GET /api/runs/:id/atelier-pattern` envoie (§5.7)
 * et ce que `POST /api/runs/:id/actions` accepte dans `action.atelier` (§6.3).
 *
 * ⚠️ **Ces formes ne sont PAS celles de SPEC-API §6.2/§6.3.** §6.2 décrit les mini-jeux d'avant
 * l'avenant ⟦A-3⟧ (fenêtres de tir au Tournage, curseur sinusoïdal à la Miniature). Les formes
 * qui font foi sont celles de `server/src/atelier/patterns.ts` et
 * `server/src/atelier/timelines.ts` — c'est-à-dire les mini-jeux que le client joue vraiment.
 * Ce fichier en est le **miroir**, et rien d'autre : aucune règle de jeu n'est calculée ici.
 *
 * ## Les deux sens
 *
 * 1. **Descendant** — le pattern. Le serveur a déjà appliqué le scaling par palier, les assists
 *    et la tolérance : le client rend, il ne recalcule pas (SPEC-UI §4.1). Un seul secret ne
 *    descend jamais : **la heatmap de La Découpe**. ⟦C52 · A-170⟧ La manche d'écriture, elle,
 *    descend entière (la suite de blocs et la colonne attendue de chacun) : l'écran en a besoin
 *    pour sonner à l'instant où un bloc touche le sol — le score reste recalculé serveur.
 * 2. **Montant** : la timeline. On ne soumet **jamais un score** : des placements, des appuis,
 *    des frappes, des réglages, des clips. Le serveur régénère le pattern et refait le calcul
 *    (§6.4).
 *
 * ## Les bornes de plausibilité, et pourquoi elles sont recopiées ici
 *
 * `server/src/atelier/validate.ts` neutralise (score 0) une timeline « inhumaine » et refuse
 * (`422`) une timeline trop fournie. Ces bornes contraignent donc la **capture** côté client :
 * un marqueur de repérage qui suit un coup de souris au pixel près, un curseur qui saute de 0 à 100 en une
 * frame, ou un échantillonnage à 240 Hz produisent une soumission valide au sens du jeu mais
 * refusée au sens du serveur. Les valeurs ci-dessous sont donc **des contraintes de capture**,
 * pas des règles de jeu — mais elles sont dupliquées, et c'est une dette : leur place est
 * `shared/`, d'où les deux côtés les liraient. Signalé dans le rapport de session.
 */

import type {
  AtelierStage,
  BlocEcriture,
  ColonneId,
  PlacementEcriture,
  PriseParfaite,
  VideoFormat,
} from "@maxime/shared";

import { appelApi } from "./http";

// ─────────────────────────────────────────────────────────────────────────────
// 1. Descendant — les 5 patterns (§5.7)
// ─────────────────────────────────────────────────────────────────────────────

/** L'enveloppe commune des 5 patterns (SPEC-UI §5.0). */
export interface EnveloppePatternApi {
  stage: AtelierStage;
  format: VideoFormat;
  /** Exigence E — `exigenceAtelier(state).k`, calculée par le serveur. */
  tier: number;
  /** Graine hexadécimale tronquée — affichage debug uniquement. */
  seed: string;
  /** Le serveur a **déjà** appliqué les +30 % de la tolérance. */
  toleranceApplied: boolean;
  /**
   * ⟦A-83 · SPEC-UI §7.4⟧ `true` quand le serveur a généré la **variante posée** — parce qu'on
   * la lui a demandée (`reduced_motion=1`). Les cinq `depuisServeur` la lisent pour rendre le
   * mode posé ; la soumission la redit (`reduced_motion: true`) pour que le scoreur régénère
   * exactement ce pattern-là.
   */
  reducedMotion: boolean;
  assists: { minigame: string; param: string; value: number }[];
  /** Plafond de score de l'étape (85 en tolérance ; le plafond ⟦A-9⟧ sinon). */
  scoreCap: number;
}

export type PlateformeIdApi = "toktok" | "instapix" | "metube_shorts";
export type TypeMomentApi = "humour" | "wow" | "info";
export type PisteApi = "A" | "B" | "C";

/** ⟦C47 · A-168 §2⟧ Les variantes du Montage : des cuts par deux, ou entre les temps. */
export type VarianteMontageApi = "classique" | "doubles" | "contretemps";

/**
 * ⟦C52 · A-170 §1⟧ **La manche d'écriture** : la suite de blocs qui tombent, décidée par le serveur
 * (miroir de `PatternEcriture`, `server/src/atelier/patterns.ts`). Les blocs, leurs colonnes
 * attendues, leurs instants d'apparition et leurs vitesses de chute viennent du fil ; le client
 * les rend, il ne tire rien.
 */
export interface PatternEcritureApi extends EnveloppePatternApi {
  stage: "ecriture";
  params: {
    /** Les colonnes du plateau, dans l'ordre : trois au début, quatre plus tard. */
    colonnes: ColonneId[];
    blocs: BlocEcriture[];
    /** La durée de la manche : le dernier bloc a touché le sol, plus un souffle. */
    dureeMs: number;
    /** *Plume d'or* : blocs ratés pardonnés au barème. */
    erreursPardonnees: number;
    /** Facteur de la chute rapide (↓) : celui que le serveur suppose pour juger un placement. */
    chuteRapide: number;
    /** Ce que la manche contient réellement. */
    pieges: number;
    doublets: number;
  };
}

/**
 * ⟦C53 · A-170 §2⟧ **« La prise parfaite »** : le pattern du Tournage : cinq prises, chacune avec
 * ses propres paramètres (curseur, zone verte, variante). La forme d'une prise est celle de
 * `shared/` (`PriseParfaite`), **la même** que le serveur génère et note : le client n'a pas de
 * jumeau à tenir à jour, il importe le type et la fonction du curseur.
 */
export interface PatternTournageApi extends EnveloppePatternApi {
  stage: "tournage";
  params: {
    /** Les cinq prises : la zone rétrécit et le curseur accélère de l'une à l'autre. */
    prises: PriseParfaite[];
    /** *Multicam* : N prises ratées effacées de la moyenne (`0` = aucun avantage). */
    pardons: number;
    /** *Intro signature* : plancher de points de la première prise (`0` = aucun avantage). */
    premiereAssuree: number;
  };
}

/** ⟦A-59⟧ La phase de dérushage, ou `null` quand le nœud n'est pas acheté. */
export interface ReperageApi {
  chronoMs: number;
  marqueurs: number;
  rayonMs: number;
  departs: number[];
}

export interface PatternMontageApi extends EnveloppePatternApi {
  stage: "montage";
  /** ⟦C47 · A-168 §2⟧ La variante servie (absente = `classique`). */
  variante?: VarianteMontageApi;
  params: {
    bpm: number;
    beatMs: number;
    durationMs: number;
    windowMs: number;
    leadInMs: number;
    previewMs: number;
    trackCount: 1 | 2 | 3;
    /**
     * ⟦C47⟧ `type` ne sert qu'au rendu : `double` (le second d'une paire) ou `contretemps`
     * (entre deux temps). Le barème ne le lit pas, un cut est un cut, à `atMs`.
     */
    cuts: { atMs: number; track?: PisteApi; type?: "double" | "contretemps" }[];
    /** ⟦A-59⟧ `null` ⇒ le Montage d'avant, à la milliseconde près. */
    reperage: ReperageApi | null;
    /** ⟦C38 · A-161 §3⟧ La piste musicale tirée par le serveur, ou `null` (silence : mode posé). */
    piste: string | null;
    /** ⟦C50⟧ Toujours 1 depuis C50 : la musique n'est plus étirée. */
    playbackRate: number;
    /** ⟦C50 · A-169 §2⟧ L'instant du FICHIER qui est le zéro du jeu. Absent d'un serveur d'avant C50 (0). */
    origineMs?: number;
    /** ⟦C50⟧ La grille du morceau est fiable : métronome et BPM affichés. Absent d'un serveur d'avant C50 (vrai). */
    pulsation?: boolean;
  };
}

/** ⟦A-69⟧ La phase d'annonce, ou `null` quand le nœud n'est pas acheté. */
export interface PromesseApi {
  chronoMs: number;
  largeurMin: number;
  largeurMax: number;
  departCentre: number;
  departLargeur: number;
  seuilMensonge: number;
}

export interface PatternMiniatureApi extends EnveloppePatternApi {
  stage: "miniature";
  params: {
    curseurs: { id: string; gauche: string; droite: string; poids: number; couplage: number }[];
    chronoMs: number;
    centre: number;
    zone: number;
    /** ⟦A-69⟧ `null` ⇒ la Miniature d'avant, au centième près. */
    promesse: PromesseApi | null;
    drift: number;
    raideur: number;
    amortissement: number;
    tremblement: number;
    tremblementPhase: number;
    bruitAffichage: number;
    /** Pas d'intégration **imposé** : client et serveur doivent intégrer pareil. */
    pasPhysiqueMs: number;
  };
  /** Secret d'**affichage** (la frontière rouge), pas secret de score : il descend. */
  seuilMensonge: number;
}

export interface ZoneChaudeApi {
  debut: number;
  largeur: number;
  intensite: number;
  type: TypeMomentApi;
}

/** ⟦A-69⟧ La phase de répartition, ou `null` quand le nœud n'est pas acheté. */
export interface MetrageApi {
  chronoMs: number;
  budget: number;
  min: number;
  max: number;
  departs: number[];
}

export interface PatternPromotionApi extends EnveloppePatternApi {
  stage: "promotion";
  params: {
    chronoMs: number;
    clips: number;
    longueurClip: number;
    /** ⟦A-69⟧ `null` ⇒ la Promotion d'avant, à la fraction près. */
    metrage: MetrageApi | null;
    energieParClip: number;
    plateformes: { id: PlateformeIdApi; label: string; affinite: TypeMomentApi }[];
    zonesRevelees: ZoneChaudeApi[];
    zonesClignotent: boolean;
    marqueurs: number[];
    onde: number[];
  };
  /** ⚠️ Absent du fil : `zones` (la heatmap) est **LE** secret du jeu. */
}

export interface PatternsServeur {
  turn: number;
  difficulty_tier: number;
  patterns: {
    ecriture: PatternEcritureApi;
    tournage: PatternTournageApi;
    montage: PatternMontageApi;
    miniature: PatternMiniatureApi;
    promotion: PatternPromotionApi;
  };
}

/**
 * §5.7 — les 5 patterns seedés du tour courant.
 *
 * Fonction **pure** de `(seed, turn, step, tier, assists, tolérance)` : la rappeler ne re-roule
 * rien (anti save-scum, PLAN §6). `format` et `subject_id` ne participent pas à la graine — ils
 * choisissent les valeurs effectives (chrono d'un short, zone resserrée d'un sujet chaos).
 */
export function patternsAtelier(
  runId: string,
  params: {
    format: VideoFormat;
    subject_id?: string;
    scores_precedents?: readonly number[];
    /** ⟦A-83⟧ `prefers-reduced-motion` : demander la variante posée (SPEC-UI §7.4). */
    reduced_motion?: boolean;
  },
): Promise<PatternsServeur> {
  const q = new URLSearchParams({ format: params.format });
  if (params.subject_id) q.set("subject_id", params.subject_id);
  if (params.scores_precedents?.length) {
    q.set("scores_precedents", params.scores_precedents.join(","));
  }
  if (params.reduced_motion) q.set("reduced_motion", "1");
  return appelApi(`/runs/${encodeURIComponent(runId)}/atelier-pattern?${q.toString()}`);
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Montant — les 5 timelines (§6.3), miroir de `server/src/atelier/timelines.ts`
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⟦C52 · A-170⟧ Le journal de placements : une entrée par bloc que le joueur a touché, avec la
 * colonne où il s'est posé et l'instant où il a touché le sol. Un bloc sans entrée est tombé là où
 * il est apparu. Jamais un score : le serveur régénère la manche et refait le calcul.
 */
export interface TimelineEcriture {
  placements: PlacementEcriture[];
}

/**
 * ⟦C53⟧ Un appui par prise, au plus : `prise` est le rang (0 à 4), `atMs` l'instant de l'appui en
 * millisecondes **depuis que le curseur de CETTE prise a commencé à bouger**. Une prise sans appui
 * (le curseur est allé au bout) est absente. Jamais un score, jamais une position de curseur : le
 * serveur la recalcule par la fonction du temps de `shared/`.
 */
export interface TimelineTournage {
  appuis: { prise: number; atMs: number }[];
}

export interface TimelineMontage {
  hits: { atMs: number; track?: PisteApi }[];
  /**
   * ⟦A-59⟧ Les **commandes** du joueur pendant le repérage — jamais l'élargissement qu'elles
   * achètent ni la note qu'elles rapportent : le serveur régénère les cuts et refait tout.
   * `position` est une fraction de piste (0-1). Absent quand le nœud n'ouvre pas la phase.
   */
  reperage?: {
    poses: { marqueur: number; atMs: number; position: number }[];
    finishedAtMs: number;
  };
}

export interface TimelineMiniature {
  reglages: { atMs: number; valeurs: number[] }[];
  /**
   * ⟦A-69⟧ Les **fourchettes successives tracées** pendant la phase — jamais la note qu'elles
   * rapportent : le serveur rejoue la dernière, la reborne, en fait le sweet spot, puis rejoue
   * le dosage par-dessus. Absent quand le nœud n'ouvre pas la phase.
   */
  promesse?: {
    poses: { atMs: number; centre: number; largeur: number }[];
    finishedAtMs: number;
  };
}

export interface TimelinePromotion {
  clips: { debut: number; plateforme: PlateformeIdApi }[];
  submittedAtMs?: number;
  /**
   * ⟦A-69⟧ Les **tailles successives commandées** pendant le métrage — jamais les longueurs
   * finales : le serveur rejoue la dernière commande de chaque clip, reborne, et renormalise si
   * la somme dépasse le budget. Le client ne peut pas s'offrir du métrage. Absent quand le nœud
   * n'ouvre pas la phase.
   */
  metrage?: {
    poses: { clip: number; atMs: number; longueur: number }[];
    finishedAtMs: number;
  };
}

export type TimelineAtelier =
  | TimelineEcriture
  | TimelineTournage
  | TimelineMontage
  | TimelineMiniature
  | TimelinePromotion;

/**
 * Une étape soumise : soit les gestes joués, soit une délégation (§6.3).
 *
 * ⟦A-4 E · M3.30⟧ `freelance_id` : QUI on embauche au marché. Optionnel côté contrat (absent,
 * le serveur retient l'offre la moins chère — les appelants d'avant M3.30 restent valides),
 * mais l'écran du marché l'envoie toujours : un joueur choisit une personne, pas un tarif.
 */
export type EtapeSoumise =
  | { timeline: TimelineAtelier }
  | { delegated: true; freelance_id?: string };

export interface SoumissionAtelier {
  ecriture: EtapeSoumise;
  tournage: EtapeSoumise;
  montage: EtapeSoumise;
  /** Longues seulement — un short n'a ni miniature ni promotion (PLAN §4.5). */
  miniature?: EtapeSoumise;
  promotion?: EtapeSoumise;
  /**
   * ⟦A-83 · SPEC-UI §7.4⟧ Les gestes ont été joués sur la variante posée : le serveur la
   * régénère pour noter, et plafonne à 85. Jamais envoyé à `false` — absent est l'état neutre.
   */
  reduced_motion?: true;
}

/**
 * La timeline d'une étape **qu'aucun geste n'a jouée** — celle du curseur stub.
 *
 * Elle est vide, et c'est exactement ce qu'elle doit être : le serveur la notera pour ce qu'elle
 * vaut. Un stub ne peut pas produire une vraie vidéo, et ce n'est pas un problème à contourner.
 */
export function timelineVide(stage: AtelierStage): TimelineAtelier {
  switch (stage) {
    case "ecriture":
      return { placements: [] };
    case "tournage":
      return { appuis: [] };
    case "montage":
      return { hits: [] };
    case "miniature":
      return { reglages: [] };
    case "promotion":
      return { clips: [] };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Les bornes de capture — recopiées de `server/src/atelier/validate.ts`
// ─────────────────────────────────────────────────────────────────────────────

/** Deux gestes **discrets** à moins de 80 ms ⇒ le serveur neutralise l'étape (score 0). */
export const ECART_HUMAIN_MIN_MS = 80;
/** Trace **continue** : en deçà, ce n'est plus un rendu, c'est une trace fabriquée. */
export const ECHANTILLON_MIN_MS = 8;
/**
 * Période d'échantillonnage visée pour les traces continues. Au-dessus du minimum serveur, et
 * assez lâche pour qu'un écran 240 Hz ne dépasse pas le plafond de `1,25 × durée / 8`.
 */
export const ECHANTILLON_VISE_MS = 14;
/** `len(ts) > 1,25 × taille_pattern` ⇒ `422 invalid_timeline` : la requête entière est perdue. */
export const FACTEUR_TAILLE_MAX = 1.25;
/** Anti-téléportation, en largeurs par seconde. ⟦C53⟧ Plus de cadre au Tournage : le repérage du Montage la garde. */
export const VITESSE_CADRE_MAX = 4;
/** Vitesse maximale d'un curseur du putaclic-o-mètre, en points par seconde. */
export const VITESSE_CURSEUR_MAX = 500;
/**
 * Marge de sûreté appliquée aux deux vitesses ci-dessus **côté capture**.
 *
 * Le serveur compare `d / dt` à la borne exacte ; un client qui vise pile la borne la franchit
 * au premier arrondi de `performance.now()`. On borne donc un peu en dessous — un coup de
 * souris trop vif fait traîner le cadre, il ne fait pas perdre la vidéo.
 */
export const MARGE_VITESSE = 0.85;

/**
 * Combien de gestes discrets une étape peut porter avant que le serveur ne refuse la requête.
 * `taille` est la taille du pattern au sens de §6.4 (cuts pour le Montage, blocs pour l'Écriture).
 */
export function gestesMax(taille: number): number {
  return Math.floor(FACTEUR_TAILLE_MAX * Math.max(1, taille));
}

/**
 * L'horodateur d'une suite de gestes **discrets**.
 *
 * Le serveur exige des instants **strictement croissants** et espacés d'au moins
 * `ECART_HUMAIN_MIN_MS`. Deux stratégies, et le choix dépend de ce que l'instant signifie :
 *
 * - **Il porte le score** (les frappes du Montage) ⇒ on ne le déplace pas d'un millimètre :
 *   `accepte()` rejette la frappe trop rapprochée, qui n'aurait de toute façon servi aucun cut.
 * - **Il ne porte pas le score** (les gestes d'un jeu dont seul l'ordre compte) ⇒
 *   `pousse()` décale l'instant vers l'avant pour tenir la contrainte. Le geste a bien eu lieu ;
 *   c'est son horodatage qui est arrondi, et aucun barème ne le lit.
 */
export function creerHorodateur(ecartMinMs = ECART_HUMAIN_MIN_MS) {
  let dernier = Number.NEGATIVE_INFINITY;
  return {
    accepte(atMs: number): number | null {
      const t = Math.round(atMs);
      if (t - dernier < ecartMinMs) return null;
      dernier = t;
      return t;
    },
    pousse(atMs: number): number {
      const t = Math.max(Math.round(atMs), dernier + ecartMinMs);
      dernier = t;
      return t;
    },
  };
}
